import { Message } from '../../../types/models';
import { APIError } from '../../error/errorHandler';
import { formatMessagesForMistral, formatMistralResponse } from './messageFormatter';
import { MistralChatRequest, MistralChatResponse, MistralEmbeddingRequest, MistralEmbeddingResponse, MistralMessage } from './types';
import { getProviderConfig, getModelInfo } from '../../../config/modelsDatabase';
import { usageService } from '../../../services/UsageService';
import { useStore } from '../../../store/useStore';

export class MistralClient {
  private apiKey: string;
  private baseUrl: string;
  private chatEndpoint: string;
  private embeddingsEndpoint: string;

  constructor(apiKey: string) {
    if (!apiKey) {
      throw new APIError('API key is required', 'Mistral');
    }

    // Get Mistral configuration
    const config = getProviderConfig('mistral');
    if (!config) {
      throw new APIError('Mistral provider configuration not found', 'Mistral');
    }

    this.apiKey = apiKey;
    this.baseUrl = config.baseUrl;
    this.chatEndpoint = config.endpoints?.chat || '/api/mistral-chat';
    this.embeddingsEndpoint = config.endpoints?.embeddings || '/api/mistral-embeddings';
  }

  async chat(messages: Message[], modelId: string): Promise<{ message: Message; usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number } }> {
    try {
      const formattedMessages = formatMessagesForMistral(messages);
      
      const request: MistralChatRequest = {
        model: modelId,
        messages: formattedMessages,
        temperature: 0.7,
        top_p: 1,
        stream: false,
        max_tokens: 2048
      };

      // Use fetch to call the proxy endpoint instead of Mistral SDK
      const response = await fetch(`${this.baseUrl}${this.chatEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `API error occurred: Status ${response.status} Content-Type ${response.headers.get('Content-Type')} Body ${JSON.stringify(errorData)}`
        );
      }
      
      // Parse the raw response
      const rawResponse = await response.json();
      
      // Convert the raw response to our expected type
      const parsedResponse: MistralChatResponse = {
        id: rawResponse.id || '',
        object: rawResponse.object || '',
        created: rawResponse.created || Date.now(),
        model: rawResponse.model || modelId,
        choices: (rawResponse.choices || []).map(choice => ({
          index: choice.index,
          message: choice.message as MistralMessage,
          finishReason: choice.finish_reason || choice.finishReason || 'stop'
        })),
        usage: {
          promptTokens: rawResponse.usage?.prompt_tokens || rawResponse.usage?.promptTokens || 0,
          completionTokens: rawResponse.usage?.completion_tokens || rawResponse.usage?.completionTokens || 0,
          totalTokens: rawResponse.usage?.total_tokens || rawResponse.usage?.totalTokens || 0
        }
      };
      
      if (!parsedResponse.choices?.[0]?.message) {
        throw new Error('Invalid response from Mistral API');
      }

      return {
        message: formatMistralResponse(parsedResponse.choices[0].message),
        usage: {
          prompt_tokens: parsedResponse.usage.promptTokens,
          completion_tokens: parsedResponse.usage.completionTokens || 0,
          total_tokens: parsedResponse.usage.totalTokens
        }
      };
    } catch (error) {
      console.error('Error in Mistral chat:', error);
      throw new APIError(
        error instanceof Error ? error.message : 'Failed to get response from Mistral',
        'Mistral'
      );
    }
  }

  async createEmbeddings(texts: string[]): Promise<number[][]> {
    try {
      // Get small model info from database
      const smallModel = getModelInfo('mistral-small-latest');
      if (!smallModel) {
        throw new Error('Mistral small model configuration not found');
      }

      const request: MistralEmbeddingRequest = {
        model: 'mistral-small-latest',
        input: texts
      };

      // Use fetch to call the proxy endpoint instead of Mistral SDK
      const response = await fetch(`${this.baseUrl}${this.embeddingsEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `API error occurred: Status ${response.status} Content-Type ${response.headers.get('Content-Type')} Body ${JSON.stringify(errorData)}`
        );
      }
      
      // Parse the raw response
      const rawResponse = await response.json();
      
      // Convert the raw response to our expected type
      const parsedResponse: MistralEmbeddingResponse = {
        id: rawResponse.id || '',
        object: rawResponse.object || '',
        data: (rawResponse.data || []).map(item => ({
          object: item.object || '',
          embedding: item.embedding || [],
          index: item.index || 0
        })),
        model: rawResponse.model || 'mistral-small-latest',
        usage: {
          promptTokens: rawResponse.usage?.prompt_tokens || rawResponse.usage?.promptTokens || 0,
          totalTokens: rawResponse.usage?.total_tokens || rawResponse.usage?.totalTokens || 0
        }
      };

      if (!parsedResponse.data) {
        throw new Error('Invalid response from Mistral Embeddings API');
      }

      return parsedResponse.data.map(item => item.embedding);
    } catch (error) {
      console.error('Error in Mistral embeddings:', error);
      throw new APIError(
        error instanceof Error ? error.message : 'Failed to get embeddings from Mistral',
        'Mistral'
      );
    }
  }

  async verifyApiKey(): Promise<boolean> {
    try {
      // Try to create an embedding as a test request
      await this.createEmbeddings(['test']);
      return true;
    } catch (error) {
      return false;
    }
  }
}

export async function sendMessage(
  message: string,
  apiKey: string,
  modelId: string,
  context: { systemPrompt: string },
  previousMessages: Message[]
): Promise<string> {
  const client = new MistralClient(apiKey);
  
  // Base system prompt for code formatting
  const baseSystemPrompt = `You must format all code examples using markdown code blocks with explicit language identifiers.

For example, Python code should be formatted as:
\`\`\`python
def example():
    print("Hello World")
\`\`\`

JavaScript code should be formatted as:
\`\`\`javascript
function example() {
    console.log("Hello World");
}
\`\`\`

Always specify the language in the code block syntax.`;

  const messages: Message[] = [
    // Always include base system prompt with code formatting instructions
    {
      id: 'system-1',
      role: 'system' as const,
      content: context.systemPrompt ? `${baseSystemPrompt}\n\n${context.systemPrompt}` : baseSystemPrompt,
      timestamp: Date.now(),
      modelId
    },
    // Add previous messages
    ...previousMessages,
    // Add current message
    {
      id: Date.now().toString(),
      role: 'user' as const,
      content: message,
      timestamp: Date.now(),
      modelId
    }
  ];

  const { message: responseMessage, usage } = await client.chat(messages, modelId);

  // Record usage if we have a user
  const store = useStore.getState();
  if (store.user && usage) {
    await usageService.recordUsage(store.user.uid, modelId, usage, store.topUpValue);
  }

  return responseMessage.content;
}